// src/features/disciplinaryIncidentAction/disciplinaryIncidentActionSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";

// --- Async Thunks ---

// Fetch all disciplinary incident actions
export const fetchDisciplinaryIncidentActions = createAsyncThunk(
  "disciplinaryIncidentAction/fetchAll",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/fetch/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // if the server returns 404 (e.g. “no actions found”), treat it as empty list
      if (response.status === 404) {
        return [];
      }
      if (!response.ok) throw new Error("Failed to fetch incident actions");
      return await response.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);
export const fetchDisciplinaryIncidentActionsByParent = createAsyncThunk(
  "disciplinaryIncidentAction/fetchDisciplinaryIncidentActionsByParent",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/fetch/parent/Account/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // if the server returns 404 (e.g. “no actions found”), treat it as empty list
      if (response.status === 404) {
        return [];
      }
      if (!response.ok) throw new Error("Failed to fetch incident actions");
      return await response.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

export const fetchDisciplinaryIncidentActionsByStudent = createAsyncThunk(
  "disciplinaryIncidentAction/fetchDisciplinaryIncidentActionsByStudent",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/fetch/student/Account/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // if the server returns 404 (e.g. “no actions found”), treat it as empty list
      if (response.status === 404) {
        return [];
      }
      if (!response.ok) throw new Error("Failed to fetch incident actions");
      return await response.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Create a new disciplinary incident action
export const createDisciplinaryIncidentAction = createAsyncThunk(
  "disciplinaryIncidentAction/create",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error("Creation failed");
      return await response.json();
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Hard-delete a disciplinary incident action
export const deleteDisciplinaryIncidentAction = createAsyncThunk(
  "disciplinaryIncidentAction/delete",
  async (incidentActionID, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/hardDelete/${incidentActionID}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Delete failed");
      return incidentActionID;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Update parental meeting status
export const editParentalMeeting = createAsyncThunk(
  "disciplinaryIncidentAction/editParentalMeeting",
  async (incidentActionID, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/edit/parentalMeeting/${incidentActionID}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Parental meeting update failed");
      return incidentActionID;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Update counseling status
export const editCounseling = createAsyncThunk(
  "disciplinaryIncidentAction/editCounseling",
  async (incidentActionID, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/edit/counseling/${incidentActionID}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Counseling update failed");
      return incidentActionID;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// Update hearing status
export const editHearing = createAsyncThunk(
  "disciplinaryIncidentAction/editHearing",
  async (incidentActionID, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/DisciplinaryIncidentAction/edit/hearing/${incidentActionID}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Hearing update failed");
      return incidentActionID;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.message);
    }
  }
);

// --- Slice ---
const disciplinaryIncidentActionSlice = createSlice({
  name: "disciplinaryIncidentAction",
  initialState: {
    items: [],
    status: "idle",
    error: null,
    formData: {
      incidentId: "",
      actionId: "",
      actionDate: "",
      startDate: "",
      endDate: "",
      actionTakenBy: "",
      notes: "",
      status: "Pending",
      requiresParentalMeeting: false,
      requiresCounseling: false,
      requiresHearing: false,
    },
    modalVisible: false,
    editingId: null,
  },
  reducers: {
    setFormData(state, action) {
      state.formData = action.payload;
    },
    resetFormData(state) {
      state.formData = {
        incidentId: "",
        actionId: "",
        actionDate: "",
        startDate: "",
        endDate: "",
        actionTakenBy: "",
        notes: "",
        status: "Pending",
        requiresParentalMeeting: false,
        requiresCounseling: false,
        requiresHearing: false,
      };
      state.editingId = null;
    },
    setEditingAction(state, action) {
      state.editingId = action.payload;
    },
    setModalVisible(state, action) {
      state.modalVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDisciplinaryIncidentActions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDisciplinaryIncidentActions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchDisciplinaryIncidentActions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //,
      .addCase(fetchDisciplinaryIncidentActionsByStudent.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchDisciplinaryIncidentActionsByStudent.fulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.items = action.payload;
        }
      )
      .addCase(
        fetchDisciplinaryIncidentActionsByStudent.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
        }
      )
      .addCase(fetchDisciplinaryIncidentActionsByParent.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchDisciplinaryIncidentActionsByParent.fulfilled,
        (state, action) => {
          state.status = "succeeded";
          state.items = action.payload;
        }
      )
      .addCase(
        fetchDisciplinaryIncidentActionsByParent.rejected,
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
        }
      )
      .addCase(createDisciplinaryIncidentAction.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(deleteDisciplinaryIncidentAction.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (itm) => itm.incidentActionID !== action.payload
        );
      })
      .addCase(editParentalMeeting.fulfilled, (state, action) => {
        const idx = state.items.findIndex(
          (i) => i.incidentActionID === action.payload
        );
        if (idx !== -1) state.items[idx].parentalHearingStatus = 1;
      })
      .addCase(editCounseling.fulfilled, (state, action) => {
        const idx = state.items.findIndex(
          (i) => i.incidentActionID === action.payload
        );
        if (idx !== -1) state.items[idx].counselingStatus = 1;
      })
      .addCase(editHearing.fulfilled, (state, action) => {
        const idx = state.items.findIndex(
          (i) => i.incidentActionID === action.payload
        );
        if (idx !== -1) state.items[idx].hearingStatus = 1;
      });
  },
});

export const { setFormData, resetFormData, setEditingAction, setModalVisible } =
  disciplinaryIncidentActionSlice.actions;

export default disciplinaryIncidentActionSlice.reducer;
